import {isEqualWith, uniqBy} from 'lodash';
import {type ActionType as TSActionType, getType} from 'typesafe-actions';

import {fromServerResult, getLastUpdatedWithWindow} from '../../util/runDeltas';
import * as Run from '../../util/runs';
import {parseGqlDeltaOp} from '../runs-low/api';
import {type RunSelectorRunsData} from './actions';
import * as Actions from './actions';
import {deleteRun, updateRun, updateRuns, updateRunTags} from './utils';

export const EMPTY_DATA: RunSelectorRunsData = {
  lastUpdatedAt: new Date(0).toISOString(),
  runs: [],
  totalRuns: 0,
  pinnedRuns: [],
};

export type StateType = {
  /**
   * There may be multiple active RunSelectorRun queries on a single page.
   * For example, a report may contain multiple panel grids, which each
   * have a dedicated run selector with its own RSR results. Another example
   * is if grouping is applied, each expanded group is a sub-query.
   * Each key in this state represents the results of a distinct RSR query.
   */
  [queryId: string]: RunSelectorRunsData;
};

export type ActionType = TSActionType<typeof Actions>;

function isExactValueEqual(left: unknown, right: unknown) {
  // Preserve Object.is number semantics: NaN is stable, but +0 and -0 differ.
  if (typeof left === 'number' && typeof right === 'number') {
    return Object.is(left, right);
  }
  // fromServerResult rebuilds the runs array but reuses unchanged run objects.
  if (
    Array.isArray(left) &&
    Array.isArray(right) &&
    left.length === right.length &&
    left.every((value, index) => Object.is(value, right[index]))
  ) {
    return true;
  }
  return undefined;
}

export function runSelectorRuns(
  state: StateType = {},
  action: ActionType
): StateType {
  switch (action.type) {
    case getType(Actions.processQueryResults): {
      const {
        queryId,
        data: {runs, pinnedRuns},
      } = action.payload;
      const existing = state[queryId] ?? EMPTY_DATA;

      const incoming = {
        delta: runs.delta.map(d => parseGqlDeltaOp(d)),
        totalRuns: runs.totalCount,
        deltaOrder: runs.deltaOrder,
      };
      const combined = fromServerResult(incoming, existing);
      combined.runs = uniqBy(combined.runs, 'id');
      combined.lastUpdatedAt = getLastUpdatedWithWindow(combined.lastUpdatedAt);
      const nextData: RunSelectorRunsData = {
        ...combined,
        pinnedRuns: pinnedRuns?.map(run => Run.fromJson(run)!) ?? [],
      };

      if (
        state[queryId] != null &&
        isEqualWith(existing, nextData, isExactValueEqual)
      ) {
        return state;
      }

      return {
        ...state,
        [queryId]: nextData,
      };
    }
    case getType(Actions.loadRunSelectorRunsFromCache): {
      const {queryId, data} = action.payload;
      return {
        ...state,
        [queryId]: data,
      };
    }
    case getType(Actions.removeQuery): {
      const {queryId} = action.payload;
      if (!state[queryId]) {
        return state;
      }
      const nextState = Object.assign({}, state);
      delete nextState[queryId];
      return nextState;
    }
    case getType(Actions.deleteRun): {
      const {runName} = action.payload;
      return updateRuns(state, runs => deleteRun(runs, runName));
    }
    case getType(Actions.updateRun): {
      const {id: runName, vars} = action.payload;
      return updateRuns(state, runs => updateRun(runs, runName, vars));
    }
    case getType(Actions.updateRunTags): {
      const {tagMap} = action.payload;
      return updateRuns(state, runs => updateRunTags(runs, tagMap));
    }
    default: {
      return state;
    }
  }
}
